import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { TrendingUp, TrendingDown, Activity, Calendar, Target, BarChart3, Clock, CheckCircle } from "lucide-react";

export default function PlanVsActual() {
  return (
    <div>
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Plan vs Actual</h2>
            <p className="text-muted-foreground">Compare planned visitor targets with actual performance</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="button-export">
              <BarChart3 className="w-4 h-4 mr-2" />
              Export Report
            </Button>
            <Button data-testid="button-refresh">
              <Activity className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Key Performance Indicators */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card data-testid="card-daily-target">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Daily Target</CardTitle>
              <Target className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">50</div>
              <div className="flex items-center text-xs text-muted-foreground">
                <span className="text-green-600 font-medium">31 actual</span>
                <span className="mx-1">•</span>
                <span>62% achieved</span>
              </div>
              <Progress value={62} className="mt-2" data-testid="progress-daily" />
            </CardContent>
          </Card>

          <Card data-testid="card-weekly-target">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Weekly Target</CardTitle>
              <Calendar className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">350</div>
              <div className="flex items-center text-xs text-muted-foreground">
                <span className="text-green-600 font-medium">298 actual</span>
                <span className="mx-1">•</span>
                <span>85% achieved</span>
              </div>
              <Progress value={85} className="mt-2" data-testid="progress-weekly" />
            </CardContent>
          </Card>

          <Card data-testid="card-efficiency">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Efficiency Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">92%</div>
              <p className="text-xs text-muted-foreground flex items-center">
                <TrendingUp className="w-3 h-3 mr-1" />
                +5% from last week
              </p>
            </CardContent>
          </Card>

          <Card data-testid="card-variance">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Variance</CardTitle>
              <TrendingDown className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">-15%</div>
              <p className="text-xs text-muted-foreground">Below target</p>
            </CardContent>
          </Card>
        </div>

        {/* Department Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Department Performance</CardTitle>
              <CardDescription>Plan vs actual visitor numbers by department</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { dept: "Weighbridge Operations", planned: 120, actual: 108, percentage: 90 },
                  { dept: "Loading & Unloading", planned: 80, actual: 85, percentage: 106 },
                  { dept: "Fuel Services", planned: 45, actual: 42, percentage: 93 },
                  { dept: "Maintenance Area", planned: 25, actual: 18, percentage: 72 },
                  { dept: "Administrative", planned: 30, actual: 35, percentage: 117 }
                ].map((item, index) => (
                  <div key={index} className="space-y-2" data-testid={`dept-performance-${index}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{item.dept}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {item.actual}/{item.planned}
                        </span>
                        <Badge 
                          variant={item.percentage >= 100 ? "default" : item.percentage >= 80 ? "secondary" : "destructive"}
                          data-testid={`badge-dept-${index}`}
                        >
                          {item.percentage}%
                        </Badge>
                      </div>
                    </div>
                    <Progress value={item.percentage} className="h-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Time-based Analysis</CardTitle>
              <CardDescription>Hourly planned vs actual visitor arrivals</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { time: "06:00 - 08:00", planned: 15, actual: 12, status: "below" },
                  { time: "08:00 - 10:00", planned: 20, actual: 23, status: "above" },
                  { time: "10:00 - 12:00", planned: 25, actual: 24, status: "on-track" },
                  { time: "12:00 - 14:00", planned: 18, actual: 15, status: "below" },
                  { time: "14:00 - 16:00", planned: 22, actual: 26, status: "above" },
                  { time: "16:00 - 18:00", planned: 12, actual: 8, status: "below" }
                ].map((slot, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`time-slot-${index}`}>
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="font-medium">{slot.time}</p>
                        <p className="text-sm text-muted-foreground">
                          Planned: {slot.planned} | Actual: {slot.actual}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {slot.status === "above" && <TrendingUp className="h-4 w-4 text-green-500" />}
                      {slot.status === "below" && <TrendingDown className="h-4 w-4 text-red-500" />}
                      {slot.status === "on-track" && <CheckCircle className="h-4 w-4 text-blue-500" />}
                      <Badge 
                        variant={slot.status === "above" ? "default" : slot.status === "on-track" ? "secondary" : "destructive"}
                        data-testid={`badge-time-${index}`}
                      >
                        {slot.status.replace("-", " ")}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Metrics */}
        <Card>
          <CardHeader>
            <CardTitle>Performance Metrics</CardTitle>
            <CardDescription>Detailed analysis of visitor flow and facility utilization</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-muted-foreground">VISITOR THROUGHPUT</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm">Average Processing Time</span>
                    <span className="font-medium">12 minutes</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Peak Hour Capacity</span>
                    <span className="font-medium">85%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Queue Wait Time</span>
                    <span className="font-medium text-green-600">3 minutes</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-muted-foreground">FACILITY UTILIZATION</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm">Weighbridge A</span>
                    <span className="font-medium">78%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Weighbridge B</span>
                    <span className="font-medium">65%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Loading Bays</span>
                    <span className="font-medium text-red-600">92%</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-muted-foreground">COMPLIANCE METRICS</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm">On-time Arrivals</span>
                    <span className="font-medium text-green-600">87%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Documentation Complete</span>
                    <span className="font-medium">94%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Safety Compliance</span>
                    <span className="font-medium text-green-600">98%</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Items */}
        <Card>
          <CardHeader>
            <CardTitle>Action Items</CardTitle>
            <CardDescription>Recommended actions to improve plan vs actual performance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-red-600">HIGH PRIORITY</h4>
                <div className="space-y-2">
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="font-medium text-red-700">Maintenance Area Underperformance</p>
                    <p className="text-sm text-red-600">28% below target - investigate staffing and equipment issues</p>
                  </div>
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="font-medium text-red-700">Evening Shift Shortfall</p>
                    <p className="text-sm text-red-600">16:00-18:00 slot consistently underperforming</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-green-600">OPPORTUNITIES</h4>
                <div className="space-y-2">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="font-medium text-green-700">Loading Bay Optimization</p>
                    <p className="text-sm text-green-600">6% above target - consider expanding capacity</p>
                  </div>
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="font-medium text-green-700">Morning Peak Efficiency</p>
                    <p className="text-sm text-green-600">Successfully handling 15% above planned volume</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}